import { useEffect, useState } from 'react'
import { Icon } from '@iconify/react'
import clsx from 'clsx'
import { useModal } from '@ebay/nice-modal-react'
import { useQueryClient } from '@tanstack/react-query'
import { BulkInProgressModal, Input, useMessage, Dropdown } from '@pepper/ui'

import {
  useBulkUpdateItemStatus,
  useBulkUpdateOrderStatus,
  useUpdateItemStatus,
} from 'utils/hooks/workflows'
import { AddEditStatusModal } from '../AddEditStatusModal/AddEditStatusModal'
import s from './StatusDropdown.module.css'

type Props = {
  companyId: string
  statusList?: WorkflowStatus[]
  itemStatusId?: string
  itemId?: string
  bulkItems?: Item[] | Assignment[]
  isPrivateDoc: boolean
  children: JSX.Element
}

export const StatusDropdown = ({
  companyId,
  statusList,
  itemStatusId,
  itemId,
  bulkItems,
  isPrivateDoc,
  children,
}: Props) => {
  const [showStatusChoiceDropdown, setShowStatusChoiceDropdown] =
    useState(false)
  const [selectedItem, setSelectedItem] = useState(itemStatusId)
  const [statusData, setStatusData] = useState(statusList ?? [])
  const addStatusModal = useModal(AddEditStatusModal)
  const { showSuccess, showError } = useMessage()
  const queryClient = useQueryClient()
  const bulkProgressModal = useModal(BulkInProgressModal)
  const { mutateAsync: bulkItemStatusUpdate } = useBulkUpdateItemStatus()
  const { mutateAsync: bulkOrderStatusUpdate } = useBulkUpdateOrderStatus()
  const { mutateAsync: updateItemStatus } = useUpdateItemStatus()

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const filteredStatusList = statusList?.filter(status =>
      status.name.toLowerCase().includes(event.target.value)
    )
    setStatusData(filteredStatusList ?? [])
  }

  const handleClick = async (statusId: string) => {
    if (itemId) {
      let response
      if (isPrivateDoc) {
        response = await updateItemStatus({
          itemId,
          workflowStatusId: statusId,
        })
      } else {
        response = await bulkOrderStatusUpdate({
          assignments: [
            {
              id: itemId,
              workflow_status_id: statusId,
            },
          ],
          companyId,
        })
      }

      if (response.status === 'success') {
        showSuccess('Status updated successfully!')
      } else {
        showError('Something went wrong!')
      }
    } else if (bulkItems && bulkItems.length > 0) {
      const bulkPayload = bulkItems.map(item => {
        return {
          id: item.id,
          workflow_status_id: statusId,
        }
      })
      let response
      if (isPrivateDoc) {
        response = await bulkItemStatusUpdate({
          items: bulkPayload,
          companyId,
        })
      } else {
        response = await bulkOrderStatusUpdate({
          assignments: bulkPayload,
          companyId,
        })
      }
      if (response.status === 'success') {
        bulkProgressModal.show({
          transactionId: response.transaction_id,
          isBulkWorkflow: true,
        })
      } else {
        showError('Could not update all selected items')
      }
    }
    setSelectedItem(statusId)
    setShowStatusChoiceDropdown(false)
    queryClient.invalidateQueries([isPrivateDoc ? 'doc-items' : 'order-items'])
    !isPrivateDoc && queryClient.invalidateQueries([`/assignments/:${itemId}`])
  }

  useEffect(() => {
    setStatusData(statusList ?? [])
  }, [statusList])

  return (
    <Dropdown
      overlay={
        <div className={s.container}>
          <Input
            prefix={<Icon icon="lucide:search" className={s.searchIcon} />}
            className={s.inputBox}
            placeholder="Change status to..."
            onChange={handleSearch}
          />
          <div className={s.listArea}>
            {statusData.map(item => (
              <div
                key={item.id}
                className={clsx(s.item, {
                  [s.selected]: item.id === selectedItem,
                })}
                role="none"
                onClick={() => handleClick(item.id)}
              >
                <div className={s.itemLeft}>
                  <div
                    className={clsx(s.itemColor, {
                      [s.noColor]: item.name === 'No Status',
                    })}
                    style={{
                      backgroundColor: `#${item?.color}`,
                      border: `3px solid #${item?.color}25`,
                    }}
                  />
                  <div>{item.name}</div>
                </div>
                {item.id === selectedItem && (
                  <Icon icon="mdi:tick" className={s.tickIcon} />
                )}
              </div>
            ))}
          </div>
          <div className={s.divider} />
          <div
            className={s.addNewStatus}
            role="none"
            onClick={() => {
              setShowStatusChoiceDropdown(false)
              addStatusModal.show({
                statusList,
                companyId,
                onSuccess: status => {
                  setStatusData([...statusData, status])
                },
              })
            }}
          >
            <span className={s.addNewStatusText}>+ New Status</span>
          </div>
        </div>
      }
      placement="topCenter"
      trigger={['click']}
      visible={showStatusChoiceDropdown}
      onVisibleChange={() => {
        if (showStatusChoiceDropdown) setShowStatusChoiceDropdown(false)
        else setShowStatusChoiceDropdown(true)
      }}
    >
      {children}
    </Dropdown>
  )
}
